/**
 * Treasures of Time — Open-Bid Auction Engine (Client)
 * Fetches auction state via App Proxy, handles bidding, countdown, and polling.
 */
(function () {
  'use strict';

  var PROXY_BASE = '/apps/auction/api';
  var POLL_INTERVAL = 15000; // 15s
  var pollTimer = null;

  // ── Helpers ──────────────────────────────────────────────────────────

  function formatGBP(amount) {
    return '\u00a3' + Number(amount).toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function $(selector, ctx) {
    return (ctx || document).querySelector(selector);
  }

  function $$(selector, ctx) {
    return (ctx || document).querySelectorAll(selector);
  }

  // ── Init ─────────────────────────────────────────────────────────────

  function initAuction(panel) {
    var productId = panel.getAttribute('data-product-id');
    if (!productId) return;

    fetchState(productId, panel);

    // Start polling for live auctions
    pollTimer = setInterval(function () {
      fetchState(productId, panel, true);
    }, POLL_INTERVAL);
  }

  // ── Fetch State ──────────────────────────────────────────────────────

  function fetchState(productId, panel, isPolling) {
    fetch(PROXY_BASE + '/auction/state?productId=' + encodeURIComponent(productId))
      .then(function (res) { return res.json(); })
      .then(function (data) {
        renderState(panel, data, productId);
        if (!isPolling) {
          // Remove skeleton
          var skeleton = $('.tot-auction-panel__skeleton', panel);
          if (skeleton) skeleton.style.display = 'none';
          var content = $('.tot-auction-panel__content', panel);
          if (content) content.style.display = '';
        }
      })
      .catch(function (err) {
        console.error('[TOT Auction] State fetch failed:', err);
      });
  }

  // ── Render State ─────────────────────────────────────────────────────

  function renderState(panel, state, productId) {
    // Status
    var statusDot = $('.tot-auction-panel__status-dot', panel);
    var statusText = $('.tot-auction-panel__status-text', panel);
    if (statusDot) {
      statusDot.className = 'tot-auction-panel__status-dot tot-auction-panel__status-dot--' + state.status;
    }
    if (statusText) {
      var label = state.status.charAt(0).toUpperCase() + state.status.slice(1);
      statusText.textContent = 'Status: ' + label.replace('_', ' ');
    }
    panel.setAttribute('data-auction-status', state.status);

    // Highest bid
    var highestEl = $('.tot-auction-panel__highest-bid', panel);
    if (highestEl) {
      highestEl.textContent = state.highestBid != null
        ? formatGBP(state.highestBid)
        : 'No bids yet';
    }

    // Reserve indicator
    var reserveEl = $('.tot-auction-panel__reserve-indicator', panel);
    if (reserveEl) {
      reserveEl.textContent = state.reserveMet ? 'Reserve met' : 'Reserve not yet met';
      reserveEl.className = 'tot-auction-panel__reserve-indicator' +
        (state.reserveMet ? ' tot-auction-panel__reserve-indicator--met' : '');
    }

    // Bid count
    var countEl = $('.tot-auction-panel__bid-count', panel);
    if (countEl) {
      countEl.textContent = state.bidCount + ' bid' + (state.bidCount !== 1 ? 's' : '');
    }

    // Min bid hint
    var hintEl = $('.tot-auction-panel__min-hint', panel);
    if (hintEl) {
      hintEl.textContent = 'Minimum bid: ' + formatGBP(state.nextMinBid);
    }

    // Bid input min attribute
    var bidInput = $('.tot-auction-panel__bid-input', panel);
    if (bidInput) {
      bidInput.setAttribute('min', state.nextMinBid);
      bidInput.setAttribute('placeholder', formatGBP(state.nextMinBid));
    }

    // Countdown (use server time to correct drift)
    var countdown = $('.tot-auction-panel__countdown', panel);
    if (countdown) {
      startCountdown(countdown, state.endTime, state.serverTime, panel);
    }

    // Disable bid form if not live
    var bidForm = $('.tot-auction-panel__bid-form', panel);
    if (bidForm) {
      var submitBtn = $('.tot-auction-panel__bid-submit', bidForm);
      if (submitBtn) {
        submitBtn.disabled = state.status !== 'live';
      }
      if (bidInput) {
        bidInput.disabled = state.status !== 'live';
      }
    }

    // Stop polling if settled/ended statuses
    if (['settled', 'reserve_not_met', 'cancelled'].indexOf(state.status) !== -1) {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    }
  }

  // ── Countdown ────────────────────────────────────────────────────────

  var countdownTimer = null;

  function startCountdown(el, endTime, serverTime, panel) {
    if (countdownTimer) clearInterval(countdownTimer);

    var serverNow = new Date(serverTime).getTime();
    var clientNow = Date.now();
    var drift = serverNow - clientNow;
    var endTs = new Date(endTime).getTime();

    function render() {
      var correctedNow = Date.now() + drift;
      var remaining = endTs - correctedNow;

      if (remaining <= 0) {
        el.textContent = 'Auction ended';
        panel.setAttribute('data-auction-status', 'ended');
        var statusDot = $('.tot-auction-panel__status-dot', panel);
        var statusText = $('.tot-auction-panel__status-text', panel);
        if (statusDot) statusDot.className = 'tot-auction-panel__status-dot tot-auction-panel__status-dot--ended';
        if (statusText) statusText.textContent = 'Status: Ended';
        clearInterval(countdownTimer);
        return;
      }

      var totalSec = Math.floor(remaining / 1000);
      var d = Math.floor(totalSec / 86400);
      var h = Math.floor((totalSec % 86400) / 3600);
      var m = Math.floor((totalSec % 3600) / 60);
      var s = totalSec % 60;

      el.textContent = d + 'd ' + h + 'h ' + m + 'm ' + s + 's';
    }

    render();
    countdownTimer = setInterval(render, 1000);
  }

  // ── Submit Bid ───────────────────────────────────────────────────────

  function submitBid(productId, amount, panel) {
    var feedback = $('.tot-auction-panel__bid-feedback', panel);
    var submitBtn = $('.tot-auction-panel__bid-submit', panel);

    if (feedback) {
      feedback.textContent = 'Placing bid...';
      feedback.className = 'tot-auction-panel__bid-feedback tot-auction-panel__bid-feedback--pending';
    }
    if (submitBtn) submitBtn.disabled = true;

    fetch(PROXY_BASE + '/auction/bid', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId: productId, amount: Number(amount) })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, status: res.status, data: data };
        });
      })
      .then(function (result) {
        if (result.ok && result.data.accepted) {
          if (feedback) {
            feedback.textContent = 'Bid accepted! You are the highest bidder at ' + formatGBP(result.data.yourBid) + '.';
            feedback.className = 'tot-auction-panel__bid-feedback tot-auction-panel__bid-feedback--success';
          }
          // Refresh state
          fetchState(productId, panel);
          // Refresh bid history if visible
          var historyEl = $('.tot-auction-panel__bid-history', panel);
          if (historyEl && historyEl.style.display !== 'none') {
            fetchBidHistory(productId, panel);
          }
        } else {
          var errorMsg = result.data.error || 'Bid not accepted.';
          if (result.status === 409 && result.data.nextMinBid) {
            errorMsg += ' Minimum bid: ' + formatGBP(result.data.nextMinBid) + '.';
          }
          if (feedback) {
            feedback.textContent = errorMsg;
            feedback.className = 'tot-auction-panel__bid-feedback tot-auction-panel__bid-feedback--error';
          }
          // Update min hint from server response
          if (result.data.nextMinBid) {
            var hintEl = $('.tot-auction-panel__min-hint', panel);
            if (hintEl) hintEl.textContent = 'Minimum bid: ' + formatGBP(result.data.nextMinBid);
            var bidInput = $('.tot-auction-panel__bid-input', panel);
            if (bidInput) {
              bidInput.setAttribute('min', result.data.nextMinBid);
              bidInput.setAttribute('placeholder', formatGBP(result.data.nextMinBid));
            }
          }
        }
      })
      .catch(function (err) {
        console.error('[TOT Auction] Bid error:', err);
        if (feedback) {
          feedback.textContent = 'Something went wrong. Please try again.';
          feedback.className = 'tot-auction-panel__bid-feedback tot-auction-panel__bid-feedback--error';
        }
      })
      .finally(function () {
        if (submitBtn) submitBtn.disabled = false;
      });
  }

  // ── Bid History ──────────────────────────────────────────────────────

  function fetchBidHistory(productId, panel) {
    var historyList = $('.tot-auction-panel__bid-list', panel);
    if (!historyList) return;

    fetch(PROXY_BASE + '/auction/bids?productId=' + encodeURIComponent(productId))
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (!data.bids || data.bids.length === 0) {
          historyList.innerHTML = '<li class="tot-auction-panel__bid-empty">No bids yet.</li>';
          return;
        }

        historyList.innerHTML = data.bids.map(function (bid) {
          var dateStr = new Date(bid.createdAt).toLocaleString('en-GB', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
          });
          var label = bid.isMine ? ' <span class="tot-auction-panel__bid-you">(You)</span>' : '';
          return '<li class="tot-auction-panel__bid-item">' +
            '<span class="tot-auction-panel__bid-amount">' + formatGBP(bid.amount) + label + '</span>' +
            '<span class="tot-auction-panel__bid-time">' + dateStr + '</span>' +
            '</li>';
        }).join('');
      })
      .catch(function (err) {
        console.error('[TOT Auction] Bid history error:', err);
      });
  }

  // ── Boot ─────────────────────────────────────────────────────────────

  function boot() {
    var panels = $$('.tot-auction-panel[data-product-id]');
    panels.forEach(function (panel) {
      initAuction(panel);

      // Bind bid form
      var form = $('.tot-auction-panel__bid-form', panel);
      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var input = $('.tot-auction-panel__bid-input', form);
          if (!input || !input.value) return;
          submitBid(panel.getAttribute('data-product-id'), input.value, panel);
        });
      }

      // Bind history toggle
      var toggle = $('.tot-auction-panel__history-toggle', panel);
      if (toggle) {
        toggle.addEventListener('click', function () {
          var history = $('.tot-auction-panel__bid-history', panel);
          if (!history) return;

          var isVisible = history.style.display !== 'none';
          history.style.display = isVisible ? 'none' : '';
          toggle.setAttribute('aria-expanded', String(!isVisible));

          if (!isVisible) {
            fetchBidHistory(panel.getAttribute('data-product-id'), panel);
          }
        });
      }
    });
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
